// Shared helpers for the Leave Management wallchart / calendar views.
import dayjs from 'dayjs';

export const FIXED_TYPE_COLORS = {
    1: '#c2185b', // Vacation Leave (Holiday) — magenta, Timetastic-style
    2: '#ef6c00', // Sick Leave — amber
    6: '#00897b', // Accumulated Vacation — teal
};

export const TYPE_PALETTE = ['#5e35b1', '#3949ab', '#00838f', '#2e7d32', '#c62828', '#6d4c41', '#455a64', '#ad1457'];

export const APPROVED_STATUS_ID = 2;

// "Work From Home" means present-but-remote, not away. Matched by name so it
// survives a different type id between environments. Rendered with its own
// colour and an outlined (not solid) block + house glyph on the wallchart /
// calendar so a manager reads it at a glance as "in, working from home".
export const WFH_TYPE_NAME = 'Work From Home';
export const WFH_TYPE_COLOR = '#2e7d32';

export function isWfhType(type) {
    return !!type && String(type.name || '').trim().toLowerCase() === WFH_TYPE_NAME.toLowerCase();
}

// A request is WFH if its embedded leave_type, or the matching entry in the
// reference list, is the Work From Home type.
export function isWfhRequest(request, leaveTypes = []) {
    if (!request) return false;
    if (isWfhType(request.leave_type)) return true;
    const id = request.leave_type_id || (request.leave_type && request.leave_type.id);
    return isWfhType((leaveTypes || []).find((t) => t.id === id));
}

// Roles allowed into the Leave Management dashboard (1 = Super Admin,
// 2 = Admin, 11 = Management) and the app's god-mode user ids.
export const LEAVE_MANAGER_ROLE_IDS = [1, 2, 11];
// 1 = Dillon (dev), 122 = Daniza (Finance Manager, uses all tabs), 145/177 = app god-mode
export const LEAVE_UNRESTRICTED_USER_IDS = [1, 122, 145, 177];

// Unrestricted = sees every employee (Super Admin / god-mode).
export function isLeaveSuperAdmin(user) {
    return !!user && (user.role_id === 1 || LEAVE_UNRESTRICTED_USER_IDS.includes(user.id));
}

// Can open the Leave Management dashboard at all. Mirrors the API's
// LeaveScopeTrait::canViewLeaveDashboard — manager roles, or anyone set up as a
// team manager (roleManagers) or with direct reports.
export function canAccessLeaveManagement(user, roleManagers = []) {
    if (!user) return false;
    if (isLeaveSuperAdmin(user)) return true;
    if (LEAVE_MANAGER_ROLE_IDS.includes(user.role_id)) return true;
    if (user.role_members && user.role_members.some((m) => LEAVE_MANAGER_ROLE_IDS.includes(m.user_role_id))) {
        return true;
    }
    if ((roleManagers || []).some((rm) => rm.manager_id === user.id)) return true;
    return !!(user.managed_users && user.managed_users.length > 0);
}

// Stable colour for a leave type id. `leaveTypes` (the reference list) is used so
// unknown types keep the same colour regardless of which requests are on screen.
export function colorForType(id, leaveTypes = []) {
    if (isWfhType((leaveTypes || []).find((t) => t.id === id))) return WFH_TYPE_COLOR;
    if (FIXED_TYPE_COLORS[id]) return FIXED_TYPE_COLORS[id];
    const ids = (leaveTypes || []).map((t) => t.id).filter((x) => !FIXED_TYPE_COLORS[x]);
    const idx = ids.indexOf(id);
    const pos = idx >= 0 ? idx : (Number(id) || 0);
    return TYPE_PALETTE[pos % TYPE_PALETTE.length];
}

export function fullName(user) {
    if (!user) return 'Unknown User';
    if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.name || user.email || 'Unknown User';
}

export function initials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
}

export function profilePicUrl(user) {
    const pic = user && user.profile_picture;
    if (!pic) return null;
    if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
    let path = pic.startsWith('/media/') ? pic.replace('/media/', '/storage/') : pic;
    path = path.startsWith('/') ? path : `/${path}`;
    const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
    return `${base}${path}`;
}

// Strip a pointless trailing .0 while keeping halves (e.g. 12, 12.5).
export function fmtNum(n) {
    const v = Number(n) || 0;
    return Number.isInteger(v) ? v : Math.round(v * 10) / 10;
}

export function isApproved(request) {
    return ((request.status && request.status.id) || request.leave_status_id) === APPROVED_STATUS_ID;
}

export function requestUserId(request) {
    return request.user_id || (request.user && request.user.id);
}

export const PENDING_STATUS_ID = 1;

export function requestStatusId(request) {
    return (request.status && request.status.id) || request.leave_status_id;
}

export function buildDays(startKey, count, todayKey) {
    const letters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const start = dayjs(startKey);
    const out = [];
    for (let i = 0; i < count; i++) {
        const d = start.add(i, 'day');
        const dow = d.day();
        const key = d.format('YYYY-MM-DD');
        out.push({
            key,
            dayNum: d.date(),
            dow,
            letter: letters[dow],
            isWeekend: dow === 0 || dow === 6,
            isToday: key === todayKey,
        });
    }
    return out;
}

function remainingAllowance(balances, userId, year) {
    const rec = (balances || []).find((u) => u.id === userId);
    if (!rec || !rec.get_leaves || !rec.get_leaves.length || year === null) return null;
    const rows = rec.get_leaves.filter((l) => l.year === year);
    if (!rows.length) return null;
    const rem = rows.reduce((s, l) => s + ((l.leave_count || 0) - (l.used_leaves || 0)), 0);
    return Number.isInteger(rem) ? rem : Math.round(rem * 10) / 10;
}

export function buildWallchartRows({ users, requests, days, leaveTypes, balances = [], year = null, includePending = false }) {
    const startKey = days[0].key;
    const endKey = days[days.length - 1].key;
    const map = {};

    const drawable = (requests || []).filter((r) => {
        const id = requestStatusId(r);
        return id === APPROVED_STATUS_ID || (includePending && id === PENDING_STATUS_ID);
    });
    // pending first so an approved request on the same day overwrites it
    drawable.sort((a, b) => (requestStatusId(a) === PENDING_STATUS_ID ? -1 : 1) - (requestStatusId(b) === PENDING_STATUS_ID ? -1 : 1));

    drawable.forEach((r) => {
        const f = (r.from || '').substring(0, 10);
        const t = (r.to || '').substring(0, 10);
        if (!f || !t || t < startKey || f > endKey) return;
        const uid = requestUserId(r);
        if (!uid) return;
        const seg = {
            request: r,
            color: colorForType(r.leave_type_id || (r.leave_type && r.leave_type.id), leaveTypes),
            isHalf: !!r.is_half_day && f === t,
            side: r.half_day_period === 'PM' ? 'pm' : 'am',
            isWfh: isWfhRequest(r, leaveTypes),
            isPending: requestStatusId(r) === PENDING_STATUS_ID,
        };
        map[uid] = map[uid] || {};
        days.forEach((d) => {
            if (d.key >= f && d.key <= t) map[uid][d.key] = seg;
        });
    });

    return (users || []).map((u) => {
        const um = map[u.id] || {};
        const cells = days.map((d) => {
            const base = { dayNum: d.dayNum, dow: d.dow, weekend: d.isWeekend };
            const seg = um[d.key];
            if (!seg) return base;
            return {
                ...base,
                leave: seg.request,
                color: seg.color,
                isHalf: seg.isHalf,
                side: seg.side,
                isWfh: seg.isWfh,
                isPending: seg.isPending,
            };
        });
        return {
            user: u,
            name: fullName(u),
            position: u.position_name || u.role_name || '—',
            pic: profilePicUrl(u),
            remaining: remainingAllowance(balances, u.id, year),
            cells,
        };
    });
}

export function summarizeConflicts({ memberIds, requests, from, to, requesterId, excludeRequestId = null, leaveTypes = [] }) {
    const members = new Set(memberIds || []);
    const offUserIds = new Set();
    const pendingUserIds = new Set();
    const perDay = {};
    const f0 = from.substring(0, 10);
    const t0 = to.substring(0, 10);

    (requests || []).forEach((r) => {
        const uid = requestUserId(r);
        if (!uid || uid === requesterId || r.id === excludeRequestId || !members.has(uid)) return;
        const f = (r.from || '').substring(0, 10);
        const t = (r.to || '').substring(0, 10);
        if (!f || !t || t < f0 || f > t0) return;
        const id = requestStatusId(r);
        if (id === PENDING_STATUS_ID) {
            pendingUserIds.add(uid);
            return;
        }
        if (id !== APPROVED_STATUS_ID) return;
        if (isWfhRequest(r, leaveTypes)) return; // working remotely, not off
        offUserIds.add(uid);
        let d = dayjs(f > f0 ? f : f0);
        const end = dayjs(t < t0 ? t : t0);
        const seen = perDay;
        while (!d.isAfter(end, 'day')) {
            const key = d.format('YYYY-MM-DD');
            seen[key] = seen[key] || new Set();
            seen[key].add(uid);
            d = d.add(1, 'day');
        }
    });

    const counts = {};
    Object.keys(perDay).forEach((k) => { counts[k] = perDay[k].size; });

    return {
        totalMembers: members.size - (members.has(requesterId) ? 1 : 0),
        offCount: offUserIds.size,
        offUserIds: [...offUserIds],
        perDay: counts,
        pendingOthers: [...pendingUserIds].filter((u) => !offUserIds.has(u)).length,
    };
}

// Company ids the given user record (from the users() provider, which carries
// `access[]`) is marked as manager of (company_access.is_manager).
export function managedCompanyIds(userRecord) {
    return new Set(
        ((userRecord && userRecord.access) || []).filter((a) => a.is_manager).map((a) => Number(a.company_id))
    );
}

// Team (user role) ids the user manages, from the roleManagers provider
// (user_role_managers rows: { manager_id, user_role_id }).
export function managedTeamIds(user, roleManagers = []) {
    const meId = Number(user && user.id);
    return new Set(
        (roleManagers || []).filter((rm) => Number(rm.manager_id) === meId).map((rm) => Number(rm.user_role_id))
    );
}

// Which calendar tabs on /leaveApproval a user gets. Super Admin sees both; a
// company manager only the Business Unit calendar; a team manager only the Team
// calendar; someone who is both sees both. Everyone else sees neither.
export function calendarTabAccess(user, usersList = [], roleManagers = []) {
    if (!user) return { businessUnit: false, team: false };
    if (isLeaveSuperAdmin(user)) return { businessUnit: true, team: true };
    const record = (usersList || []).find((u) => Number(u.id) === Number(user.id)) || user;
    return {
        businessUnit: managedCompanyIds(record).size > 0,
        team: managedTeamIds(user, roleManagers).size > 0,
    };
}

// Who may open /leaveApproval: Super Admin (incl. god-mode ids), anyone marked as a
// company or team manager, or anyone who still has requests waiting on them (so
// older requests addressed to a non-manager aren't stranded).
export function canAccessLeaveApproval(user, usersList = [], hasPending = false, roleManagers = []) {
    if (!user) return false;
    if (isLeaveSuperAdmin(user) || hasPending) return true;
    const tabs = calendarTabAccess(user, usersList, roleManagers);
    return tabs.businessUnit || tabs.team;
}

// Index notes by person + day. Company-wide notes (no user_id) use person id 0.
export function buildNoteIndex(notes) {
    const idx = {};
    (notes || []).forEach((n) => {
        const key = `${Number(n.user_id) || 0}|${(n.note_date || '').substring(0, 10)}`;
        (idx[key] = idx[key] || []).push(n);
    });
    return idx;
}

// Counts for the Business Unit Calendar summary strip. Only approved requests count.
// Person-days skip weekends; a single-day half day counts 0.5. `people` is the
// distinct number of members with any such day in [from, to].
export function summarizeTeamCounts({ requests, memberIds, from, to, leaveTypes = [] }) {
    const members = new Set((memberIds || []).map(Number));
    const empty = () => ({ people: new Set(), days: 0 });
    const leave = empty();
    const wfh = empty();
    const byType = {};
    (requests || []).forEach((r) => {
        if (requestStatusId(r) !== APPROVED_STATUS_ID) return;
        const uid = Number(requestUserId(r));
        if (!members.has(uid)) return;
        const f = (r.from || '').substring(0, 10);
        const t = (r.to || '').substring(0, 10);
        if (!f || !t) return;
        const isWfh = isWfhRequest(r, leaveTypes);
        const typeId = r.leave_type_id || (r.leave_type && r.leave_type.id);
        const typeName = (r.leave_type && r.leave_type.name) || ((leaveTypes || []).find((x) => x.id === typeId) || {}).name || 'Other';
        const weight = r.is_half_day && f === t ? 0.5 : 1;
        let cur = dayjs(f > from ? f : from);
        const end = dayjs(t < to ? t : to);
        while (!cur.isAfter(end, 'day')) {
            const dow = cur.day();
            if (dow !== 0 && dow !== 6) {
                const bucket = isWfh ? wfh : leave;
                bucket.people.add(uid);
                bucket.days += weight;
                if (!isWfh) {
                    byType[typeName] = byType[typeName] || { name: typeName, typeId, people: new Set(), days: 0 };
                    byType[typeName].people.add(uid);
                    byType[typeName].days += weight;
                }
            }
            cur = cur.add(1, 'day');
        }
    });

    const fin = (b) => ({ people: b.people.size, days: b.days });
    return {
        leave: fin(leave),
        wfh: fin(wfh),
        byType: Object.values(byType)
            .map((x) => ({ name: x.name, typeId: x.typeId, people: x.people.size, days: x.days }))
            .sort((a, b) => b.days - a.days),
    };
}

// Per-day buckets for the month-grid calendar: for every non-weekend day in `days`,
// the approved leave, approved WFH and pending (leave or WFH) entries of the members.
export function buildDayBuckets({ requests, memberIds, days, leaveTypes = [] }) {
    const members = new Set((memberIds || []).map(Number));
    const map = {};
    days.forEach((d) => {
        map[d.key] = { leave: [], wfh: [], pending: [] };
    });
    (requests || []).forEach((r) => {
        const status = requestStatusId(r);
        if (status !== APPROVED_STATUS_ID && status !== PENDING_STATUS_ID) return;
        const uid = Number(requestUserId(r));
        if (!members.has(uid)) return;
        const f = (r.from || '').substring(0, 10);
        const t = (r.to || '').substring(0, 10);
        if (!f || !t) return;
        const wfh = isWfhRequest(r, leaveTypes);
        days.forEach((d) => {
            if (d.isWeekend || d.key < f || d.key > t) return;
            const entry = { request: r, userId: uid, isWfh: wfh };
            const b = map[d.key];
            (status === PENDING_STATUS_ID ? b.pending : wfh ? b.wfh : b.leave).push(entry);
        });
    });
    return map;
}
